import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

// Searchable, alphabetical single-user picker. `users` = [{id,name,email,role}].
// `noneLabel` adds a leading "clear" option that returns null.
export function UserPicker({ users, value, onChange, placeholder = "— pick a user —", noneLabel, disabled, className = "", testId }) {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(() => [...users].sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || "", undefined, { sensitivity: "base" })), [users]);
  const current = users.find(u => u.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} disabled={disabled} data-testid={testId}
          className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${className}`}>
          <span className={`truncate ${current ? "" : "opacity-60"}`}>{current ? `${current.name}${current.role && current.role !== "coach" ? ` · ${current.role}` : ""}` : (value ? "Unknown user" : placeholder)}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start">
        <Command filter={(v, q) => v.toLowerCase().includes(q.toLowerCase()) ? 1 : 0}>
          <CommandInput placeholder="Type a name or email…" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList className="max-h-64">
            <CommandEmpty>No users match.</CommandEmpty>
            <CommandGroup>
              {noneLabel && (
                <CommandItem value={`__none ${noneLabel}`} onSelect={() => { onChange(null); setOpen(false); }} data-testid={testId ? `${testId}-none` : undefined}>
                  <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} /><span className="opacity-70">{noneLabel}</span>
                </CommandItem>
              )}
              {sorted.map(u => (
                <CommandItem key={u.id} value={`${u.name || ""} ${u.email || ""} ${u.role || ""}`} onSelect={() => { onChange(u.id); setOpen(false); }} data-testid={testId ? `${testId}-opt-${u.id}` : undefined}>
                  <Check className={`mr-2 h-4 w-4 ${u.id === value ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{u.name}</span>
                  {u.role && u.role !== "coach" && <span className="ml-1.5 text-xs opacity-50">· {u.role}</span>}
                  {u.email && <span className="ml-auto pl-3 text-xs opacity-40 truncate max-w-[45%]">{u.email}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
